import React, { useEffect, useState } from 'react';

import InputLabelWrapper from 'components/basic/input-label-wrapper/InputLabelWrapper';
import { Attribute, DatasetMode, MdmProjectDto, MdmProjectInputInput, MeasurementTypeDef, useGetProjectQuery, useUpdateProjectMutation } from 'gql/generated/types';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { TabPanel, TabView } from 'primereact/tabview';
import { useParams } from 'react-router-dom';
import { T } from 'services/lang/LanguageService';
import AdditionalProjectSettings from '../components/AdditionalProjectSettings';
import DeviceConfiguration from '../components/DeviceConfiguration';
import DeviceTypes from '../components/DeviceTypes';
import { errorToaster, successToaster } from '../utils/utils';

const Settings = () => {
  const [projectConfig, setProjectConfig] = useState<MdmProjectDto | null>(null);
  const [objAttributes, setObjAttributes] = useState<Attribute[]>([]);
  const [deviceAttributes, setDeviceAttributes] = useState<Attribute[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<MeasurementTypeDef[]>([]);

  const { projectId } = useParams();

  const { data } = useGetProjectQuery({ variables: { id: projectId! }, skip: !projectId });

  const [updateProjectMutation] = useUpdateProjectMutation({
    onCompleted: () => successToaster(T("alert.project-updated-successfully")),
    onError: errorToaster,
  });

  useEffect(() => {
    if (data) {
      const {filterInfo, ...rest } = data.mdmProjectById;
      setProjectConfig(rest as MdmProjectDto);
      setObjAttributes((data.mdmProjectById?.configuration?.objectConfiguration?.customFields ?? []) as Attribute[]);
      setDeviceAttributes((data.mdmProjectById?.configuration?.deviceConfiguration?.customFields ?? []) as Attribute[]);
      setDeviceTypes((data.mdmProjectById?.configuration?.deviceConfiguration?.measurementTypes ?? []) as MeasurementTypeDef[]);
    }
  }, [data]);

  const handleSubmit = () => {
    const project: MdmProjectInputInput = {
      ...projectConfig,
      configuration: {
        ...projectConfig?.configuration,
        analyticsConfiguration: {
          ...projectConfig!.configuration!.analyticsConfiguration,
        },
        deviceConfiguration: {
          customFields: deviceAttributes,
          measurementTypes: deviceTypes
        },
        objectConfiguration: {
          ...projectConfig!.configuration!.objectConfiguration,
          customFields: objAttributes,
        },
      },
    };
    updateProjectMutation({ variables: { project }, refetchQueries: ["GetProject"] });
  }

  return (
    <div className="grid p-fluid m-3 w-full mx-auto">
      <InputLabelWrapper labelName={T("word.project")} col={4}>
        <InputText
          value={projectConfig?.title ?? ""}
          type="text"
          placeholder={T("word.project-name")}
          onChange={e => setProjectConfig(prev => ({ ...prev as MdmProjectDto, title: e.target.value }))}
        />
      </InputLabelWrapper>
      <InputLabelWrapper labelName={T("mdm.dataset-mode")} col={4}>
        <Dropdown
          value={projectConfig?.datasetMode}
          placeholder={T("word.select-one")}
          options={Object.entries(DatasetMode).map(([key, value]) => ({ label: key, value }))}
          onChange={e => setProjectConfig(prev => ({ ...prev as MdmProjectDto, datasetMode: e.target.value }))}
        />
      </InputLabelWrapper>
      <div className="flex w-full justify-content-end gap-3">
        <Button
          type="submit"
          onClick={handleSubmit}
          label={T("action.update")}
          className="p-button-rounded p-button-primary w-10rem"
        />
      </div>

      <TabView className="col-12">
        <TabPanel header={T("mdm.object-attributes")}>
          <AdditionalProjectSettings attributes={objAttributes} setAttributes={setObjAttributes} />
        </TabPanel>
        <TabPanel header={T("mdm.device-attributes")}>
          <AdditionalProjectSettings attributes={deviceAttributes} setAttributes={setDeviceAttributes} />
        </TabPanel>
        <TabPanel header={T("mdm.device-types")}>
          <DeviceTypes types={deviceTypes} setTypes={setDeviceTypes} />
        </TabPanel>
        <TabPanel header={T("word.device-config")}>
          <DeviceConfiguration configurations={projectConfig?.deviceConfigs} />
        </TabPanel>
      </TabView>
    </div>
  );
}

export default Settings;
